import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Switch,
  TimePicker,
} from '@ui';
import {
  Trash2,
  Clock,
  AlertCircle,
  GripVertical,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Search,
  Save,
  Activity,
  Coins,
  Calculator,
} from 'lucide-react';
import { toastMessage } from '@ui';
import { cn } from '@ui/lib/utils';

interface ActivityType {
  id: number;
  name: string;
  color?: string;
  isActive: boolean;
  price?: number;
  defaultAmount?: number;
}

interface TemplateItemResponse {
  id: number;
  activityTypeId: number;
  activityTypeName?: string;
  activityTypeColor?: string;
  startTime: string;
  endTime?: string | null;
  isInstantComplete: boolean;
  defaultContent?: string;
  isRequired: boolean;
  order: number;
}

interface TemplateResponse {
  id: number;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  items: TemplateItemResponse[];
  price?: number;
}

interface ItemData {
  activityTypeId: number;
  startTime: string;
  endTime?: string | null;
  isInstantComplete: boolean;
  defaultContent?: string;
  isRequired: boolean;
  order: number;
}

interface ActivityTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template?: TemplateResponse | null;
  templateId?: number | null;
  activityTypes: ActivityType[];
  onSave: (data: {
    name: string;
    description?: string;
    price?: number;
    includeActivityPrice?: boolean;
    items: Array<ItemData>;
  }) => Promise<boolean> | void;
  onCreateTemplate?: (data: { name: string; description?: string; price?: number }) => Promise<number | null>;
  onUpdateTemplate?: (templateId: number, data: { name: string; description?: string; price?: number }) => Promise<boolean>;
  onAddItem?: (templateId: number, item: ItemData) => Promise<number | null>;
  onUpdateItem?: (templateId: number, itemId: number, item: ItemData) => Promise<boolean>;
  onDeleteItem?: (templateId: number, itemId: number) => Promise<boolean>;
  isCreating?: boolean;
  isItemMutating?: boolean;
  onRefetch?: () => void;
}

interface TemplateItemFormData {
  id: string;
  activityTypeId: number;
  startTime: string;
  endTime: string;
  isInstantComplete: boolean;
  defaultContent: string;
  isRequired: boolean;
}

interface DragPreview {
  activityTypeId: number;
  startTime: string;
  endTime: string;
  top: number;
  height: number;
}

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const HOUR_HEIGHT = 60;
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;

// 드래그 가능한 활동 칩
function DraggableActivityChip({
  type,
  onDragStart,
  onDragEnd,
}: {
  type: ActivityType;
  onDragStart: (e: React.DragEvent, type: ActivityType) => void;
  onDragEnd: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, type)}
      onDragEnd={onDragEnd}
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing transition-all hover:scale-105"
      style={{
        backgroundColor: `${type.color || '#6B7280'}15`,
        color: type.color || '#6B7280',
        border: `1px solid ${type.color || '#6B7280'}30`,
      }}
    >
      <GripVertical className="h-3 w-3 opacity-50" />
      {type.name}
    </div>
  );
}

// 인라인 활동 편집 폼 (활동 블록 아래에 표시)
function InlineEditForm({
  item,
  activityTypes,
  onSave,
  onCancel,
  onDelete,
  isLoading,
}: {
  item: TemplateItemFormData;
  activityTypes: ActivityType[];
  onSave: (item: TemplateItemFormData) => void;
  onCancel: () => void;
  onDelete: () => void;
  isLoading?: boolean;
}) {
  const [localItem, setLocalItem] = useState<TemplateItemFormData>({ ...item });
  const [activityTypeSearch, setActivityTypeSearch] = useState('');
  const [originalItem, setOriginalItem] = useState<TemplateItemFormData>({ ...item });

  useEffect(() => {
    setLocalItem({ ...item });
    setOriginalItem({ ...item });
    setActivityTypeSearch('');
  }, [item.id]);

  // 드래그/리사이즈에 의한 외부 시간 변경을 localItem에 동기화
  useEffect(() => {
    setLocalItem((prev) => ({
      ...prev,
      startTime: item.startTime,
      endTime: item.endTime,
    }));
  }, [item.startTime, item.endTime]);

  // 변경 여부 감지
  const hasChanges = localItem.activityTypeId !== originalItem.activityTypeId
    || localItem.startTime !== originalItem.startTime
    || localItem.endTime !== originalItem.endTime
    || localItem.isInstantComplete !== originalItem.isInstantComplete
    || localItem.defaultContent !== originalItem.defaultContent
    || localItem.isRequired !== originalItem.isRequired;

  const handleFieldChange = (updates: Partial<TemplateItemFormData>) => {
    setLocalItem((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = () => {
    onSave(localItem);
  };

  const handleCancel = () => {
    setLocalItem({ ...item });
    onCancel();
  };

  const filteredActivityTypes = activityTypes
    .filter((t) => t.isActive)
    .filter((t) => activityTypeSearch === '' || t.name.toLowerCase().includes(activityTypeSearch.toLowerCase()));

  const selectedType = activityTypes.find((t) => t.id === localItem.activityTypeId);

  return (
    <div
      className="absolute left-0 right-0 bg-white dark:bg-neutral-900 border border-orange-200 rounded-lg shadow-xl z-40 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b bg-orange-50 flex items-center justify-between">
        <span className="text-sm font-medium text-orange-700 flex items-center gap-2">
          <Activity className="h-4 w-4" />
          활동 편집
        </span>
        <div className="flex items-center gap-1">
          <button
            className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
            onClick={onDelete}
            title="삭제"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 transition-colors"
            onClick={handleCancel}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* 활동 종류 선택 */}
        <div className="space-y-1.5">
          <Label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">활동 종류</Label>
          {selectedType ? (
            <div className="flex items-center justify-between p-2 border rounded-lg bg-orange-50 border-orange-200">
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${selectedType.color || '#6B7280'}20` }}
                >
                  <Activity className="h-3 w-3" style={{ color: selectedType.color || '#6B7280' }} />
                </div>
                <span className="text-sm font-medium" style={{ color: selectedType.color || '#6B7280' }}>
                  {selectedType.name}
                </span>
              </div>
              <button
                className="p-1 rounded hover:bg-white dark:hover:bg-neutral-900 dark:bg-neutral-900"
                onClick={() => handleFieldChange({ activityTypeId: 0 })}
              >
                <X className="h-3 w-3 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
              </button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                <Input
                  placeholder="활동 종류 검색..."
                  className="pl-8 h-8 text-sm"
                  value={activityTypeSearch}
                  onChange={(e) => setActivityTypeSearch(e.target.value)}
                />
              </div>
              <div className="max-h-28 overflow-y-auto border rounded-lg">
                {filteredActivityTypes.length === 0 ? (
                  <div className="p-2 text-center text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                    {activityTypeSearch ? '검색 결과가 없습니다.' : '활동 종류가 없습니다.'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredActivityTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          handleFieldChange({ activityTypeId: type.id });
                          setActivityTypeSearch('');
                        }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 transition-colors text-left"
                      >
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${type.color || '#6B7280'}20` }}
                        >
                          <Activity className="h-3 w-3" style={{ color: type.color || '#6B7280' }} />
                        </div>
                        <span className="text-sm font-medium truncate" style={{ color: type.color || '#6B7280' }}>
                          {type.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 시간 설정 */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">시작 시간</Label>
            <TimePicker
              value={localItem.startTime}
              onChange={(v) => handleFieldChange({ startTime: v })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">종료 시간</Label>
            <TimePicker
              value={localItem.endTime}
              onChange={(v) => handleFieldChange({ endTime: v })}
              disabled={localItem.isInstantComplete}
              className="mt-1"
            />
          </div>
        </div>

        {/* 활동 내용 */}
        <div>
          <Label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">활동 내용</Label>
          <Input
            value={localItem.defaultContent}
            onChange={(e) => handleFieldChange({ defaultContent: e.target.value })}
            placeholder="예: 아침 사료 급여"
            className="mt-1 h-8 text-sm"
          />
        </div>

        {/* 옵션 토글 */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={localItem.isRequired}
              onCheckedChange={(checked) => handleFieldChange({ isRequired: checked })}
            />
            <span className="text-xs text-neutral-600 dark:text-neutral-300">필수</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={localItem.isInstantComplete}
              onCheckedChange={(checked) =>
                handleFieldChange({
                  isInstantComplete: checked,
                  endTime: checked ? '' : localItem.endTime,
                })
              }
            />
            <span className="text-xs text-neutral-600 dark:text-neutral-300">즉시 종료</span>
          </label>
        </div>

        {/* 저장/취소 버튼 */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 text-xs">
            취소
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={isLoading || !localItem.activityTypeId || !hasChanges}
            className="h-7 text-xs"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Save className="h-3 w-3 mr-1" />
            )}
            저장
          </Button>
        </div>
      </div>
    </div>
  );
}

// 타임라인 활동 블록 (편집 폼 포함, 드래그/리사이즈 지원)
function TimelineActivityBlockWithEdit({
  item,
  activityTypes,
  onSelect,
  onSave,
  onDelete,
  onCancel,
  onTimeChange,
  isEditing,
  isLoading,
  onInteractionStart,
  onInteractionEnd,
}: {
  item: TemplateItemFormData;
  activityTypes: ActivityType[];
  onSelect: () => void;
  onSave: (item: TemplateItemFormData) => void;
  onDelete: () => void;
  onCancel: () => void;
  onTimeChange: (item: TemplateItemFormData) => void;
  isEditing: boolean;
  isLoading?: boolean;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}) {
  const blockRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizingTop, setIsResizingTop] = useState(false);
  const [isResizingBottom, setIsResizingBottom] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [resizeTopOffset, setResizeTopOffset] = useState(0);
  const [resizeBottomOffset, setResizeBottomOffset] = useState(0);

  const dragStartRef = useRef({ y: 0, startMinutes: 0, endMinutes: 0 });

  const activityType = activityTypes.find((t) => t.id === item.activityTypeId);
  const color = activityType?.color || '#6B7280';
  const startMinutes = timeToMinutes(item.startTime);
  const endMinutes = item.isInstantComplete
    ? startMinutes + 15
    : item.endTime
      ? timeToMinutes(item.endTime)
      : startMinutes + 30;

  const baseTop = startMinutes * MINUTE_HEIGHT;
  const baseHeight = Math.max((endMinutes - startMinutes) * MINUTE_HEIGHT, 20);

  // 드래그/리사이즈 중 계산된 위치와 높이
  const isResizing = isResizingTop || isResizingBottom;
  const displayTop = isDragging
    ? baseTop + dragOffset
    : isResizingTop
      ? baseTop + resizeTopOffset
      : baseTop;
  const displayHeight = isResizingTop
    ? baseHeight - resizeTopOffset
    : isResizingBottom
      ? baseHeight + resizeBottomOffset
      : baseHeight;

  // 드래그/리사이즈 가능 여부 (수정 모드일 때만 가능)
  const canDragOrResize = isEditing && !item.isInstantComplete;

  // 드래그 시작 (블록 이동) - 수정 모드에서만 가능
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!canDragOrResize) return;

    e.preventDefault();
    e.stopPropagation();

    dragStartRef.current = {
      y: e.clientY,
      startMinutes,
      endMinutes,
    };
    onInteractionStart?.();
    setIsDragging(true);
    setDragOffset(0);
  }, [canDragOrResize, startMinutes, endMinutes, onInteractionStart]);

  // 위쪽 리사이즈 시작 (시작 시간 변경)
  const handleResizeTopStart = useCallback((e: React.MouseEvent) => {
    if (!canDragOrResize) return;

    e.preventDefault();
    e.stopPropagation();

    dragStartRef.current = {
      y: e.clientY,
      startMinutes,
      endMinutes,
    };
    onInteractionStart?.();
    setIsResizingTop(true);
    setResizeTopOffset(0);
  }, [canDragOrResize, startMinutes, endMinutes, onInteractionStart]);

  // 아래쪽 리사이즈 시작 (종료 시간 변경)
  const handleResizeBottomStart = useCallback((e: React.MouseEvent) => {
    if (!canDragOrResize) return;

    e.preventDefault();
    e.stopPropagation();

    dragStartRef.current = {
      y: e.clientY,
      startMinutes,
      endMinutes,
    };
    onInteractionStart?.();
    setIsResizingBottom(true);
    setResizeBottomOffset(0);
  }, [canDragOrResize, startMinutes, endMinutes, onInteractionStart]);

  // 마우스 이동 처리
  useEffect(() => {
    if (!isDragging && !isResizingTop && !isResizingBottom) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - dragStartRef.current.y;
      const deltaMinutes = Math.round(deltaY / MINUTE_HEIGHT / 15) * 15;

      if (isDragging) {
        // 드래그: 시작/종료 시간 모두 이동
        const newStartMinutes = Math.max(0, Math.min(
          dragStartRef.current.startMinutes + deltaMinutes,
          24 * 60 - (dragStartRef.current.endMinutes - dragStartRef.current.startMinutes)
        ));
        setDragOffset((newStartMinutes - dragStartRef.current.startMinutes) * MINUTE_HEIGHT);
      } else if (isResizingTop) {
        // 위쪽 리사이즈: 시작 시간 변경
        const duration = dragStartRef.current.endMinutes - dragStartRef.current.startMinutes;
        const maxDeltaUp = -dragStartRef.current.startMinutes; // 0시까지
        const maxDeltaDown = duration - 15; // 최소 15분 유지
        const clampedDelta = Math.max(maxDeltaUp, Math.min(deltaMinutes, maxDeltaDown));
        setResizeTopOffset(clampedDelta * MINUTE_HEIGHT);
      } else if (isResizingBottom) {
        // 아래쪽 리사이즈: 종료 시간 변경
        const duration = dragStartRef.current.endMinutes - dragStartRef.current.startMinutes;
        const newDuration = Math.max(15, duration + deltaMinutes); // 최소 15분
        const maxDuration = 24 * 60 - dragStartRef.current.startMinutes; // 자정까지
        const clampedDuration = Math.min(newDuration, maxDuration);
        setResizeBottomOffset((clampedDuration - duration) * MINUTE_HEIGHT);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        const deltaMinutes = Math.round(dragOffset / MINUTE_HEIGHT);
        if (deltaMinutes !== 0) {
          const newStartMinutes = startMinutes + deltaMinutes;
          const newEndMinutes = endMinutes + deltaMinutes;
          onTimeChange({
            ...item,
            startTime: minutesToTime(newStartMinutes),
            endTime: minutesToTime(newEndMinutes),
          });
        }
        setIsDragging(false);
        setDragOffset(0);
      }

      if (isResizingTop) {
        const deltaMinutes = Math.round(resizeTopOffset / MINUTE_HEIGHT);
        if (deltaMinutes !== 0) {
          const newStartMinutes = startMinutes + deltaMinutes;
          onTimeChange({
            ...item,
            startTime: minutesToTime(newStartMinutes),
          });
        }
        setIsResizingTop(false);
        setResizeTopOffset(0);
      }

      if (isResizingBottom) {
        const deltaMinutes = Math.round(resizeBottomOffset / MINUTE_HEIGHT);
        if (deltaMinutes !== 0) {
          const newEndMinutes = endMinutes + deltaMinutes;
          onTimeChange({
            ...item,
            endTime: minutesToTime(newEndMinutes),
          });
        }
        setIsResizingBottom(false);
        setResizeBottomOffset(0);
      }

      // 인터랙션 종료 알림 (약간의 딜레이로 클릭 이벤트 방지)
      setTimeout(() => {
        onInteractionEnd?.();
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
    };
  }, [isDragging, isResizingTop, isResizingBottom, dragOffset, resizeTopOffset, resizeBottomOffset, item, startMinutes, endMinutes, onTimeChange, onInteractionEnd]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      onSelect();
    }
  };

  // 실시간 시간 계산
  const getDisplayStartTime = () => {
    if (isDragging) {
      return minutesToTime(startMinutes + Math.round(dragOffset / MINUTE_HEIGHT));
    }
    if (isResizingTop) {
      return minutesToTime(startMinutes + Math.round(resizeTopOffset / MINUTE_HEIGHT));
    }
    return item.startTime;
  };

  const getDisplayEndTime = () => {
    if (isDragging) {
      return minutesToTime(endMinutes + Math.round(dragOffset / MINUTE_HEIGHT));
    }
    if (isResizingBottom) {
      return minutesToTime(endMinutes + Math.round(resizeBottomOffset / MINUTE_HEIGHT));
    }
    return item.endTime;
  };

  return (
    <div
      className="absolute left-1 right-1"
      style={{ top: `${displayTop}px` }}
    >
      {/* 활동 블록 */}
      <div
        ref={blockRef}
        className={`rounded-lg shadow-sm border-l-4 transition-shadow cursor-pointer group relative
          ${isEditing ? 'ring-2 ring-orange-400 shadow-lg' : 'hover:shadow-md'}
          ${isDragging ? 'shadow-xl opacity-90 cursor-grabbing z-50' : ''}
          ${isResizing ? 'shadow-xl z-50' : ''}`}
        style={{
          height: `${displayHeight}px`,
          backgroundColor: `${color}15`,
          borderLeftColor: color,
        }}
        onMouseDown={canDragOrResize ? handleDragStart : undefined}
        onClick={handleClick}
      >
        {/* 위쪽 리사이즈 핸들 - 수정 모드에서만 표시 */}
        {canDragOrResize && (
          <div
            className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize group/resize-top flex items-center justify-center z-10"
            onMouseDown={handleResizeTopStart}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-8 h-1 rounded-full opacity-0 group-hover:opacity-100 group-hover/resize-top:opacity-100 transition-opacity"
              style={{ backgroundColor: color }}
            />
          </div>
        )}

        <div className={cn(
          "h-full flex flex-col p-2 overflow-hidden",
          // 즉시완료 활동: 수직 가운데 정렬
          item.isInstantComplete && "justify-center"
        )}>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              {canDragOrResize && (
                <GripVertical className="h-3 w-3 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 flex-shrink-0 cursor-grab" />
              )}
              <span className="text-xs font-semibold truncate" style={{ color }}>
                {activityType?.name || '활동'}
              </span>
              {item.isRequired && (
                <span className="text-[10px] px-1 bg-red-100 text-red-600 rounded flex-shrink-0">
                  필수
                </span>
              )}
              {(activityType?.defaultAmount || activityType?.price) && (activityType?.defaultAmount || activityType?.price || 0) > 0 && (
                <span className="text-[10px] px-1 bg-orange-100 text-orange-600 rounded flex-shrink-0">
                  {((activityType?.defaultAmount || activityType?.price || 0) / 1000).toFixed(0)}천원
                </span>
              )}
              {/* 즉시완료 활동: 시간 정보를 같은 줄에 표시 */}
              {item.isInstantComplete && (
                <span className="text-[10px] text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 flex-shrink-0">
                  {item.startTime} (즉시)
                </span>
              )}
            </div>
          </div>

          {/* 일반 활동: 시간 정보를 별도 줄에 표시 */}
          {!item.isInstantComplete && (
            <div className="text-[10px] text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
              {isDragging || isResizing ? (
                // 드래그/리사이즈 중 실시간 시간 표시
                <>
                  {getDisplayStartTime()}
                  {` - ${getDisplayEndTime()}`}
                </>
              ) : (
                <>
                  {item.startTime}
                  {item.endTime && ` - ${item.endTime}`}
                </>
              )}
            </div>
          )}

          {displayHeight > 40 && item.defaultContent && (
            <div className="text-xs text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
              {item.defaultContent}
            </div>
          )}
        </div>

        {/* 아래쪽 리사이즈 핸들 - 수정 모드에서만 표시 */}
        {canDragOrResize && (
          <div
            className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize group/resize-bottom flex items-center justify-center"
            onMouseDown={handleResizeBottomStart}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-8 h-1 rounded-full opacity-0 group-hover:opacity-100 group-hover/resize-bottom:opacity-100 transition-opacity"
              style={{ backgroundColor: color }}
            />
          </div>
        )}
      </div>

      {/* 편집 폼 (활동 블록 바로 아래에 표시) - 드래그/리사이즈 중에는 숨기되 언마운트하지 않음 */}
      {isEditing && (
        <div style={{ marginTop: '4px', display: isDragging || isResizing ? 'none' : undefined }}>
          <InlineEditForm
            item={item}
            activityTypes={activityTypes}
            onSave={onSave}
            onCancel={onCancel}
            onDelete={onDelete}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

function DragPreviewBlock({
  preview,
  activityTypes,
}: {
  preview: DragPreview;
  activityTypes: ActivityType[];
}) {
  const activityType = activityTypes.find((t) => t.id === preview.activityTypeId);
  const color = activityType?.color || '#6B7280';

  return (
    <div
      className="absolute left-1 right-1 rounded-lg border-2 border-dashed pointer-events-none z-30 animate-pulse"
      style={{
        top: `${preview.top}px`,
        height: `${preview.height}px`,
        backgroundColor: `${color}20`,
        borderColor: color,
      }}
    >
      <div className="h-full flex flex-col p-2 overflow-hidden">
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold" style={{ color }}>
            {activityType?.name || '활동'}
          </span>
        </div>
        <div className="text-[10px] text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
          {preview.startTime} - {preview.endTime}
        </div>
      </div>
    </div>
  );
}

// Step 1: 템플릿 기본 정보 입력
function Step1TemplateInfo({
  name,
  setName,
  description,
  setDescription,
  price,
  setPrice,
  errors,
}: {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  price: string;
  setPrice: (v: string) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-4">
            <span className="text-2xl font-bold text-orange-500">1</span>
          </div>
          <h3 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100">템플릿 정보 입력</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">템플릿의 기본 정보를 입력해주세요.</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-sm font-medium">
              템플릿 이름 *
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 프리미엄 케어"
              className={`mt-1 ${errors.name ? 'border-red-300' : ''}`}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description" className="text-sm font-medium">
              설명
            </Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="템플릿에 대한 간단한 설명"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="price" className="text-sm font-medium">
              템플릿 기본 금액
            </Label>
            <div className="relative mt-1">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
              <Input
                id="price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="pl-9"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">템플릿 기본 요금 (선택사항)</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// 삭제 확인 모달
function DeleteConfirmModal({
  open,
  onClose,
  onConfirm,
  activityName,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  activityName?: string;
  isLoading?: boolean;
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white dark:bg-neutral-900 rounded-xl shadow-xl max-w-sm w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
            <Trash2 className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-center text-neutral-800 dark:text-neutral-100 mb-2">
            활동 삭제
          </h3>
          <p className="text-sm text-center text-neutral-600 dark:text-neutral-300">
            {activityName ? (
              <>
                <span className="font-medium text-neutral-800 dark:text-neutral-100">{activityName}</span> 활동을 삭제하시겠습니까?
              </>
            ) : (
              '이 활동을 삭제하시겠습니까?'
            )}
          </p>
          <p className="text-xs text-center text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">
            삭제된 활동은 복구할 수 없습니다.
          </p>
        </div>
        <div className="flex border-t">
          <button
            className="flex-1 px-4 py-3 text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 transition-colors"
            onClick={onClose}
            disabled={isLoading}
          >
            취소
          </button>
          <button
            className="flex-1 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors border-l flex items-center justify-center gap-2"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                삭제 중...
              </>
            ) : (
              '삭제'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Step 2: 활동 등록 (타임라인)
function Step2Activities({
  items,
  setItems,
  activityTypes,
  editingItemId,
  setEditingItemId,
  errors,
  templateId,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  isItemMutating,
  templatePrice,
  includeActivityPrice,
  setIncludeActivityPrice,
}: {
  items: TemplateItemFormData[];
  setItems: React.Dispatch<React.SetStateAction<TemplateItemFormData[]>>;
  activityTypes: ActivityType[];
  editingItemId: string | null;
  setEditingItemId: (id: string | null) => void;
  errors: Record<string, string>;
  templateId?: number | null;
  onAddItem?: (templateId: number, item: ItemData) => Promise<number | null>;
  onUpdateItem?: (templateId: number, itemId: number, item: ItemData) => Promise<boolean>;
  onDeleteItem?: (templateId: number, itemId: number) => Promise<boolean>;
  isItemMutating?: boolean;
  templatePrice: number;
  includeActivityPrice: boolean;
  setIncludeActivityPrice: (v: boolean) => void;
}) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const lastActivityAddedRef = useRef<number>(0);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [draggingTypeId, setDraggingTypeId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; itemId: string | null }>({
    open: false,
    itemId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const firstActiveActivityTypeId = activityTypes.find((t) => t.isActive)?.id || 1;

  // 활동들의 금액 합계 계산
  const totalActivityPrice = React.useMemo(() => {
    return items.reduce((sum, item) => {
      const activityType = activityTypes.find((t) => t.id === item.activityTypeId);
      return sum + (activityType?.defaultAmount || activityType?.price || 0);
    }, 0);
  }, [items, activityTypes]);

  // 최종 금액 계산: 템플릿 금액 + (활동 금액 포함 시 활동들 금액)
  const finalPrice = React.useMemo(() => {
    return templatePrice + (includeActivityPrice ? 0 : totalActivityPrice);
  }, [templatePrice, totalActivityPrice, includeActivityPrice]);

  // 드래그 시작
  const handleDragStart = (e: React.DragEvent, type: ActivityType) => {
    e.dataTransfer.setData('activityTypeId', String(type.id));
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingTypeId(type.id);
  };

  // 드래그 종료
  const handleDragEnd = () => {
    setDragPreview(null);
    setDraggingTypeId(null);
  };

  // 드래그 오버
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';

    if (!timelineRef.current || !draggingTypeId) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top + timelineRef.current.scrollTop;
    const clickedMinutes = Math.round(y / MINUTE_HEIGHT / 15) * 15;
    const clampedMinutes = Math.max(0, Math.min(clickedMinutes, 23 * 60 + 30));
    const startTime = minutesToTime(clampedMinutes);
    const endTime = minutesToTime(Math.min(clampedMinutes + 30, 24 * 60));

    setDragPreview({
      activityTypeId: draggingTypeId,
      startTime,
      endTime,
      top: clampedMinutes * MINUTE_HEIGHT,
      height: 30 * MINUTE_HEIGHT,
    });
  }, [draggingTypeId]);

  // 드래그 리브
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (timelineRef.current?.contains(relatedTarget)) return;
    setDragPreview(null);
  }, []);

  // 드롭 - 즉시 API 호출하여 추가
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    if (!timelineRef.current) return;

    const activityTypeId = e.dataTransfer.getData('activityTypeId');
    if (!activityTypeId) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top + timelineRef.current.scrollTop;
    const clickedMinutes = Math.round(y / MINUTE_HEIGHT / 15) * 15;
    const clampedMinutes = Math.max(0, Math.min(clickedMinutes, 23 * 60 + 30));
    const startTime = minutesToTime(clampedMinutes);
    const endTime = minutesToTime(Math.min(clampedMinutes + 30, 24 * 60));

    setDragPreview(null);
    setDraggingTypeId(null);

    const itemData: ItemData = {
      activityTypeId: parseInt(activityTypeId),
      startTime,
      endTime,
      isInstantComplete: false,
      defaultContent: '',
      isRequired: false,
      order: items.length + 1,
    };

    if (templateId && onAddItem) {
      const serverItemId = await onAddItem(templateId, itemData);
      if (serverItemId) {
        const newItem: TemplateItemFormData = {
          id: `server-${serverItemId}`,
          activityTypeId: parseInt(activityTypeId),
          startTime,
          endTime,
          isInstantComplete: false,
          defaultContent: '',
          isRequired: false,
        };
        setItems((prev) => [...prev, newItem]);
        setEditingItemId(newItem.id);
      }
    } else {
      const tempId = `item-${Date.now()}`;
      const newItem: TemplateItemFormData = {
        id: tempId,
        activityTypeId: parseInt(activityTypeId),
        startTime,
        endTime,
        isInstantComplete: false,
        defaultContent: '',
        isRequired: false,
      };
      setItems((prev) => [...prev, newItem]);
      setEditingItemId(tempId);
    }
  }, [setItems, setEditingItemId, templateId, onAddItem, items.length]);

  // 타임라인 클릭으로 활동 추가 - 즉시 API 호출
  const handleTimelineClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      // 인터랙션(드래그/리사이즈) 중에는 새 활동 추가 방지
      if (isInteracting) {
        return;
      }

      const target = e.target as HTMLElement;
      if (target.closest('[data-activity-block]')) {
        return;
      }

      const now = Date.now();
      if (now - lastActivityAddedRef.current < 300) {
        return;
      }

      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top + timelineRef.current.scrollTop;
      const clickedMinutes = Math.round(y / MINUTE_HEIGHT / 15) * 15;
      const clampedMinutes = Math.max(0, Math.min(clickedMinutes, 23 * 60 + 30));
      const startTime = minutesToTime(clampedMinutes);
      const endTime = minutesToTime(Math.min(clampedMinutes + 30, 24 * 60));

      lastActivityAddedRef.current = now;

      const itemData: ItemData = {
        activityTypeId: firstActiveActivityTypeId,
        startTime,
        endTime,
        isInstantComplete: false,
        defaultContent: '',
        isRequired: false,
        order: items.length + 1,
      };

      if (templateId && onAddItem) {
        const serverItemId = await onAddItem(templateId, itemData);
        if (serverItemId) {
          const newItem: TemplateItemFormData = {
            id: `server-${serverItemId}`,
            activityTypeId: firstActiveActivityTypeId,
            startTime,
            endTime,
            isInstantComplete: false,
            defaultContent: '',
            isRequired: false,
          };
          setItems((prev) => [...prev, newItem]);
          setEditingItemId(newItem.id);
        }
      } else {
        const tempId = `item-${Date.now()}`;
        const newItem: TemplateItemFormData = {
          id: tempId,
          activityTypeId: firstActiveActivityTypeId,
          startTime,
          endTime,
          isInstantComplete: false,
          defaultContent: '',
          isRequired: false,
        };
        setItems((prev) => [...prev, newItem]);
        setEditingItemId(tempId);
      }
    },
    [firstActiveActivityTypeId, setItems, setEditingItemId, isInteracting, templateId, onAddItem, items.length]
  );

  // 활동 저장 핸들러 (API 호출)
  const handleItemSave = useCallback(async (updatedItem: TemplateItemFormData) => {
    setIsSaving(true);
    try {
      setItems((prev) => prev.map((item) => (item.id === updatedItem.id ? updatedItem : item)));

      if (templateId && onUpdateItem && updatedItem.id.startsWith('server-')) {
        const itemId = parseInt(updatedItem.id.replace('server-', ''));
        await onUpdateItem(templateId, itemId, {
          activityTypeId: updatedItem.activityTypeId,
          startTime: updatedItem.startTime,
          endTime: updatedItem.isInstantComplete ? null : updatedItem.endTime || null,
          isInstantComplete: updatedItem.isInstantComplete,
          defaultContent: updatedItem.defaultContent,
          isRequired: updatedItem.isRequired,
          order: items.findIndex((i) => i.id === updatedItem.id) + 1,
        });
      } else if (templateId && onAddItem && !updatedItem.id.startsWith('server-')) {
        const serverItemId = await onAddItem(templateId, {
          activityTypeId: updatedItem.activityTypeId,
          startTime: updatedItem.startTime,
          endTime: updatedItem.isInstantComplete ? null : updatedItem.endTime || null,
          isInstantComplete: updatedItem.isInstantComplete,
          defaultContent: updatedItem.defaultContent,
          isRequired: updatedItem.isRequired,
          order: items.length,
        });

        if (serverItemId) {
          setItems((prev) =>
            prev.map((item) =>
              item.id === updatedItem.id ? { ...item, id: `server-${serverItemId}` } : item
            )
          );
        }
      }

      setEditingItemId(null);
    } finally {
      setIsSaving(false);
    }
  }, [templateId, onAddItem, onUpdateItem, items, setItems, setEditingItemId]);

  // 삭제 요청 핸들러 (새 활동은 즉시 삭제, 기존 활동은 확인 모달)
  const handleDeleteRequest = useCallback((id: string) => {
    // 서버에 저장되지 않은 새 활동은 즉시 삭제
    if (!id.startsWith('server-')) {
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingItemId === id) setEditingItemId(null);
      return;
    }
    // 서버에 저장된 활동은 확인 모달 표시
    setDeleteConfirm({ open: true, itemId: id });
  }, [editingItemId, setItems, setEditingItemId]);

  // 삭제 확인 모달 닫기
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm({ open: false, itemId: null });
  }, []);

  // 실제 삭제 실행
  const handleDeleteConfirm = useCallback(async () => {
    const id = deleteConfirm.itemId;
    if (!id) return;

    setIsDeleting(true);
    try {
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingItemId === id) setEditingItemId(null);

      if (templateId && onDeleteItem && id.startsWith('server-')) {
        const itemId = parseInt(id.replace('server-', ''));
        await onDeleteItem(templateId, itemId);
      }
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({ open: false, itemId: null });
    }
  }, [deleteConfirm.itemId, templateId, onDeleteItem, editingItemId, setItems, setEditingItemId]);

  // 편집 취소 핸들러
  const handleEditCancel = useCallback((item: TemplateItemFormData) => {
    // 새로 추가된 아이템이고 저장 안 했으면 삭제
    if (!item.id.startsWith('server-')) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
    setEditingItemId(null);
  }, [setItems, setEditingItemId]);

  // 드래그/리사이즈로 시간 변경 핸들러
  const handleTimeChange = useCallback((updatedItem: TemplateItemFormData) => {
    setItems((prev) => prev.map((item) => (item.id === updatedItem.id ? updatedItem : item)));
  }, [setItems]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* 왼쪽 패널 - 드래그 가능한 활동 목록 */}
      <div className="w-64 border-r bg-neutral-50 dark:bg-neutral-800/50 p-4 flex flex-col overflow-y-auto">
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 mb-2">
            <span className="text-xl font-bold text-orange-500">2</span>
          </div>
          <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">활동 등록</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">활동을 드래그하여 타임라인에 추가하세요.</p>
        </div>

        {/* 드래그 가능한 활동 목록 */}
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-neutral-800 dark:text-neutral-100 text-sm">활동 추가</h4>
            <span className="text-[10px] text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">드래그하여 추가</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activityTypes
              .filter((t) => t.isActive)
              .map((type) => (
                <DraggableActivityChip
                  key={type.id}
                  type={type}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              ))}
          </div>
        </div>

        {/* 사용 안내 */}
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 shadow-sm space-y-2">
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300">
            <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-orange-500">1</span>
            </div>
            <span>활동을 드래그하여 추가</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300">
            <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-orange-500">2</span>
            </div>
            <span>또는 타임라인 클릭</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300">
            <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-orange-500">3</span>
            </div>
            <span>활동 클릭하여 편집</span>
          </div>
        </div>

        <div className="mt-auto pt-4 space-y-3">
          {/* 등록된 활동 */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl p-3 shadow-sm">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">등록된 활동</div>
            <div className="text-2xl font-bold text-orange-500">{items.length}개</div>
          </div>
          {errors.items && (
            <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.items}
            </p>
          )}

          {/* 활동 금액 포함 토글 */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                <span className="text-sm text-neutral-600 dark:text-neutral-300">활동 금액 제외</span>
              </div>
              <Switch
                checked={includeActivityPrice}
                onCheckedChange={setIncludeActivityPrice}
              />
            </div>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">
              {includeActivityPrice ? '최종 금액에 활동 금액이 제외됩니다.' : '최종 금액에 활동 금액이 포함됩니다.'}
            </p>
          </div>

          {/* 금액 요약 */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl p-3 shadow-sm space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Coins className="h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
              <span className="text-neutral-600 dark:text-neutral-300">금액 정보</span>
            </div>
            <div className="space-y-1.5 pt-2 border-t">
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">템플릿 기본 금액</span>
                <span className="text-neutral-700 dark:text-neutral-200">{templatePrice.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className={`${includeActivityPrice ? 'text-neutral-400 dark:text-neutral-500 line-through' : 'text-neutral-500 dark:text-neutral-400 dark:text-neutral-500'}`}>
                  활동 금액 합계
                </span>
                <span className={`${includeActivityPrice ? 'text-neutral-400 dark:text-neutral-500 line-through' : 'text-neutral-700 dark:text-neutral-200'}`}>
                  {totalActivityPrice.toLocaleString()}원
                </span>
              </div>
              <div className="flex justify-between text-sm pt-1.5 border-t mt-1.5">
                <span className="font-medium text-neutral-700 dark:text-neutral-200">최종 금액</span>
                <span className="font-bold text-orange-600">{finalPrice.toLocaleString()}원</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 타임라인 영역 */}
      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-900">
        <div className="px-4 py-3 border-b bg-neutral-50 dark:bg-neutral-800/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">24시간 타임라인</span>
            {(isItemMutating || isSaving) && <Loader2 className="h-3 w-3 animate-spin text-orange-500" />}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
            클릭 또는 드래그하여 활동 추가
          </div>
        </div>

        <div
          ref={timelineRef}
          className={`flex-1 overflow-y-auto relative cursor-pointer ${draggingTypeId ? 'bg-orange-50/30' : ''
            }`}
          onClick={handleTimelineClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="relative" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
            {hours.map((hour) => (
              <div
                key={hour}
                className="absolute left-0 right-0 border-t border-neutral-100 dark:border-neutral-800"
                style={{ top: `${hour * HOUR_HEIGHT}px` }}
              >
                <div className="absolute left-2 top-1 text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 font-medium bg-white dark:bg-neutral-900 px-1">
                  {String(hour).padStart(2, '0')}:00
                </div>
                <div
                  className="absolute left-16 right-0 border-t border-dashed border-neutral-100 dark:border-neutral-800"
                  style={{ top: `${HOUR_HEIGHT / 2}px` }}
                />
              </div>
            ))}

            <div className="absolute left-16 right-4 top-0 bottom-0">
              {items.map((item) => (
                <div key={item.id} data-activity-block>
                  <TimelineActivityBlockWithEdit
                    item={item}
                    activityTypes={activityTypes}
                    onSelect={() => setEditingItemId(item.id)}
                    onSave={handleItemSave}
                    onDelete={() => handleDeleteRequest(item.id)}
                    onCancel={() => handleEditCancel(item)}
                    onTimeChange={handleTimeChange}
                    isEditing={editingItemId === item.id}
                    isLoading={isSaving}
                    onInteractionStart={() => setIsInteracting(true)}
                    onInteractionEnd={() => setIsInteracting(false)}
                  />
                </div>
              ))}

              {dragPreview && (
                <DragPreviewBlock preview={dragPreview} activityTypes={activityTypes} />
              )}
            </div>

            {/* 현재 시간 표시 */}
            {(() => {
              const now = new Date();
              const currentMinutes = now.getHours() * 60 + now.getMinutes();
              return (
                <div
                  className="absolute left-0 right-0 flex items-center pointer-events-none z-30"
                  style={{ top: `${currentMinutes * MINUTE_HEIGHT}px` }}
                >
                  <div className="w-16 text-right pr-2">
                    <span className="text-xs font-bold text-orange-500">지금</span>
                  </div>
                  <div className="flex-1 h-0.5 bg-orange-400" />
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      <DeleteConfirmModal
        open={deleteConfirm.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        activityName={
          deleteConfirm.itemId
            ? activityTypes.find(
              (t) => t.id === items.find((i) => i.id === deleteConfirm.itemId)?.activityTypeId
            )?.name
            : undefined
        }
        isLoading={isDeleting}
      />
    </div>
  );
}

export default function ActivityTemplateModal({
  open,
  onClose,
  template,
  templateId: propTemplateId,
  activityTypes,
  onSave,
  onCreateTemplate,
  onUpdateTemplate,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  isCreating,
  isItemMutating,
  onRefetch,
}: ActivityTemplateModalProps) {
  const isEdit = !!template;

  const [currentStep, setCurrentStep] = useState(1);
  const [createdTemplateId, setCreatedTemplateId] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [includeActivityPrice, setIncludeActivityPrice] = useState(false);
  const [items, setItems] = useState<TemplateItemFormData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);

  // Step 1 변경 감지용 원본 값
  const [originalStep1, setOriginalStep1] = useState({ name: '', description: '', price: '' });
  const isStep1Modified = name !== originalStep1.name || description !== originalStep1.description || price !== originalStep1.price;

  useEffect(() => {
    if (open) {
      if (template) {
        setCurrentStep(2);
        const tName = template.name;
        const tDesc = template.description || '';
        const tPrice = template.price !== undefined && template.price !== null ? template.price.toString() : '';
        setName(tName);
        setDescription(tDesc);
        setPrice(tPrice);
        setOriginalStep1({ name: tName, description: tDesc, price: tPrice });
        setIncludeActivityPrice(false);
        setItems(
          template.items?.map((item) => ({
            id: `server-${item.id}`,
            activityTypeId: item.activityTypeId,
            startTime: item.startTime,
            endTime: item.endTime || '',
            isInstantComplete: item.isInstantComplete,
            defaultContent: item.defaultContent || '',
            isRequired: item.isRequired,
          })) || []
        );
      } else {
        setCurrentStep(1);
        setName('');
        setDescription('');
        setPrice('');
        setOriginalStep1({ name: '', description: '', price: '' });
        setIncludeActivityPrice(false);
        setItems([]);
        setCreatedTemplateId(null);
      }
      setErrors({});
      setEditingItemId(null);
    }
  }, [template, open]);

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = '템플릿 이름을 입력해주세요';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => {
    if (!validateStep1()) return;

    // 수정 모드에서 변경사항이 없으면 API 호출 없이 다음 단계로 이동
    if (isEdit && !isStep1Modified) {
      setCurrentStep(2);
      return;
    }

    const priceValue = price ? parseInt(price, 10) : undefined;

    setIsSubmittingStep1(true);
    try {
      if (isEdit && template && onUpdateTemplate) {
        // 수정 모드: 기본 정보 업데이트 API 호출
        const success = await onUpdateTemplate(template.id, { name, description, price: priceValue });
        if (success) {
          setOriginalStep1({ name, description, price });
          setCurrentStep(2);
        }
      } else if (onCreateTemplate) {
        // 생성 모드: 템플릿 생성 API 호출
        const newId = await onCreateTemplate({ name, description, price: priceValue });
        if (newId) {
          setCreatedTemplateId(newId);
          setOriginalStep1({ name, description, price });
          setCurrentStep(2);
        }
      } else {
        setCurrentStep(2);
      }
    } finally {
      setIsSubmittingStep1(false);
    }
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (items.length === 0) {
      newErrors.items = '최소 1개 이상의 활동을 추가해주세요';
    }

    items.forEach((item) => {
      if (!item.startTime) {
        newErrors[item.id] = '시작 시간을 입력해주세요';
      } else if (!item.isInstantComplete && item.endTime) {
        const startMinutes = timeToMinutes(item.startTime);
        const endMinutes = timeToMinutes(item.endTime);
        if (startMinutes >= endMinutes) {
          newErrors[item.id] = '종료 시간은 시작 시간보다 늦어야 합니다';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!validateStep2()) return;

    const sortedItems = [...items].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );

    const priceValue = price ? parseInt(price, 10) : undefined;

    setIsSaving(true);
    try {
      const result = await onSave({
        name,
        description: description || undefined,
        price: priceValue,
        includeActivityPrice,
        items: sortedItems.map((item, index) => ({
          activityTypeId: item.activityTypeId,
          startTime: item.startTime,
          endTime: item.isInstantComplete ? null : item.endTime || null,
          isInstantComplete: item.isInstantComplete,
          defaultContent: item.defaultContent || undefined,
          isRequired: item.isRequired,
          order: index + 1,
        })),
      });

      // onSave가 Promise를 반환하지 않거나 성공한 경우
      if (result !== false) {
        toastMessage({ message: '템플릿이 저장되었습니다.', type: 'success' });
        onRefetch?.();
        onClose();
      }
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '템플릿 저장에 실패했습니다.';
      toastMessage({ message: errorMessage, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
          <DialogTitle className="text-xl flex items-center gap-3">
            {isEdit ? '템플릿 수정' : '새 템플릿 만들기'}
            <div className="flex items-center gap-2 ml-4">
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${currentStep === 1 ? 'bg-orange-100 text-orange-600' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                  }`}
                onClick={() => setCurrentStep(1)}
                disabled={isCreating}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${currentStep === 1 ? 'bg-orange-500 text-white' : 'bg-neutral-300 text-white'
                  }`}>
                  1
                </span>
                기본 정보
              </button>
              <ChevronRight className="h-4 w-4 text-neutral-300 dark:text-neutral-600 dark:text-neutral-300" />
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${currentStep === 2 ? 'bg-orange-100 text-orange-600' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                  }`}
                onClick={() => {
                  if (!isEdit && !createdTemplateId) return;
                  setCurrentStep(2);
                }}
                disabled={(!isEdit && !createdTemplateId) || isCreating}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${currentStep === 2 ? 'bg-orange-500 text-white' : 'bg-neutral-300 text-white'
                  }`}>
                  2
                </span>
                활동 등록
              </button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {currentStep === 1 && (
          <Step1TemplateInfo
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            price={price}
            setPrice={setPrice}
            errors={errors}
          />
        )}

        {currentStep === 2 && (
          <Step2Activities
            items={items}
            setItems={setItems}
            activityTypes={activityTypes}
            editingItemId={editingItemId}
            setEditingItemId={setEditingItemId}
            errors={errors}
            templateId={propTemplateId || createdTemplateId}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
            isItemMutating={isItemMutating}
            templatePrice={price ? parseInt(price, 10) : 0}
            includeActivityPrice={includeActivityPrice}
            setIncludeActivityPrice={setIncludeActivityPrice}
          />
        )}

        <DialogFooter className="px-6 py-4 border-t bg-neutral-50 dark:bg-neutral-800/50">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
              {currentStep === 2 && items.length > 0 && (
                <>
                  총 {items.length}개 활동, {items.filter((i) => i.isRequired).length}개 필수
                </>
              )}
            </div>
            <div className="flex gap-2">
              {currentStep === 1 && (
                <>
                  <Button variant="ghost" onClick={onClose} disabled={isSubmittingStep1}>
                    취소
                  </Button>
                  <Button variant="default" onClick={handleNextStep} disabled={isCreating || isSubmittingStep1}>
                    {(isCreating || isSubmittingStep1) ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isEdit ? '저장 중...' : '생성 중...'}
                      </>
                    ) : isEdit && !isStep1Modified ? (
                      <>
                        다음
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    ) : (
                      <>
                        저장 후 다음단계 이동
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </>
              )}
              {currentStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    기본 정보
                  </Button>
                  <Button variant="ghost" onClick={onClose}>
                    닫기
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
